/**
 * Sourced from Dashboard, modified for Shop.
 * https://github.com/squareup/dashboard/blob/49a226ece70f193b59011027ec6388f371f7f7b9/frontend/dashboard/app/helpers/user-journey/user-journey-result.js
 */

import { v4 as uuidV4 } from 'uuid';

/**
 * State management for a user's journey through dashboard web.
 *
 * This is a simplified, copied version of the UserJourneyResult class from
 * the UserJourneys backend:
 * https://github.com/squareup/java/blob/master/user-journeys/src/main/java/com/squareup/userjourneys/domainmodels/UserJourneyResult.kt#L6
 *
 * Experience classification is handled server-side, based on outcome/frustration/friction
 * signals here: https://github.com/squareup/java/blob/master/user-journeys/src/main/java/com/squareup/userjourneys/cdp/postprocessors/UserJourneyExperienceClassifier.kt
 */
export class UserJourneyResult {
  /**
   * @param {object} options
   * @param {string} options.entityId
   * @param {string} options.entityType
   * @param {TUserJourneyName} options.userJourneyName
   * @param {TUserJourneyVariant|undefined} options.userJourneyVariant
   */
  constructor({ entityId, entityType, userJourneyName, userJourneyVariant }) {
    this.entityType = entityType;
    this.entityId = entityId;
    this.userJourneyName = userJourneyName;
    this.id = uuidV4();
    this.createdAt = Date.now();
    this.variant = userJourneyVariant;
    this.frustrationSignals = new Set();
    this.frictionSignals = new Set();
    this.tags = new Set();
    this.terminationReason = undefined;
    this.durationRealTimeMs = undefined;
    this.outcome = undefined;
  }

  /**
   * Add a frustration signal to the user journey result.
   * @param {TFrustrationSignal} frustrationSignal
   */
  addFrustrationSignal(frustrationSignal) {
    this.frustrationSignals.add(frustrationSignal);
  }

  /**
   * Add a friction signal to the user journey result.
   * @param {TFrictionSignal} frictionSignal
   */
  addFrictionSignal(frictionSignal) {
    this.frictionSignals.add(frictionSignal);
  }

  /**
   * Add tags to the user journey result.
   * @param {Set<string>} tags
   */
  addTags(tags) {
    // merge tags into this.tags
    tags.forEach((tag) => this.tags.add(tag));
  }

  /**
   * Set the outcome of the user journey result.
   * @param {TUserJourneyOutcome} outcome
   */
  setOutcome(outcome) {
    this.outcome = outcome;
  }

  /**
   * Set the variant of the user journey result.
   * @param {TUserJourneyVariant} variant
   */
  setVariant(variant) {
    this.variant = variant;
  }

  /**
   * Set the termination reason of the user journey result.
   * @param {TTerminationReason} terminationReason
   */
  setTerminationReason(terminationReason) {
    this.terminationReason = terminationReason;
  }

  /**
   * Set the duration of the user journey result.
   * @param {number} durationRealTimeMs
   */
  setDurationRealTimeMs(durationRealTimeMs) {
    this.durationRealTimeMs = durationRealTimeMs;
  }
}
